/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import {
  Badge, Form, Modal, Spinner, Table, Nav, Tab, Button,
} from 'react-bootstrap';
import React, { useState, useEffect, useMemo } from 'react';
import {
  formatDateTime as format, formatBookingDate, formatBookingDateOnly, formatBookingTimeOnly,
} from '../../../Utils/timezone';
import thousandSeparator from '../../../Utils/numberFormat';
import {
  formatOperatorAdjustments, formatOperatorEntryCash, formatOperatorEntryPrice,
  formatOperatorPriceTotals,
} from '../../../Utils/operatorCost';
import BookingService from '../../../Services/BookingService';
import StripeBillingOverwriteModal, { differs as billingDiffers } from './StripeBillingOverwriteModal';
import MapRoutePanel from '../../../Components/MapRoutePanel';
import VersionHistoryTab from './VersionHistoryTab';
import PaymentHistory from './PaymentHistory';
import useBookingPayments from '../../../Hooks/useBookingPayments';
import PassengerMessageModal from './PassengerMessageModal';
import PassengerMessageButton from '../../../Components/PassengerMessage/PassengerMessageButton';
import { BOOKING_TYPE_LABELS, operatorRoleBadge, STOP_TYPE_LABELS } from '../../../config/bookingConstants';
import { isExpenseBooking, isTabAvailable } from '../../../Utils/bookingType';
import { get } from '../../../Services/API';
import BillingoInvoicesPanel from '../../../Components/Billingo/BillingoInvoicesPanel';
import BillingoDocumentList from '../../../Components/Billingo/BillingoDocumentList';
import { useInvoicePdf } from '../../../Components/Billingo/openInvoicePdf';
import { DOC_TYPES } from '../../../Utils/billingoDocuments';
import AskAIButton from '../../../Components/ChatWidget/AskAIButton';
import useBookingDocuments from '../../Invoicing/Components/useBookingDocuments';
import OrderLedgerTab from './OrderLedgerTab';
import DriverEventsTab from './DriverEventsTab';
import AIOverviewPanel from './AIOverviewPanel';
import TripClaimsBanner from '../../../Components/TripClaims/TripClaimsBanner';
import useBookingClaims from '../../../Hooks/useBookingClaims';
import { lineItemPrefillFor } from '../Functions/tripClaimLineItems';
import { canReviewTripClaims, finalInvoiceBlockedReason, tripClaimsOf } from '../../../Utils/tripClaims';
import isInvoicingEnabled from '../../../Utils/partnerInvoicing';

function DetailCard({ title, icon, children }) {
  return (
    <div className="detail-card">
      <div className="detail-card-header">
        <i className={`fa ${icon} me-2`} />
        {title}
      </div>
      <div className="detail-card-body">
        {children}
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className={`detail-row ${highlight ? 'bg-warning-subtle' : ''}`}>
      <span className="detail-label">
        {label}
        :
      </span>
      <span className="detail-value">{value || 'N/A'}</span>
    </div>
  );
}

// Why the Financial tab shows no per-trip invoicing panel.
//
// `isInvoicingEnabled` gates the Billingo flow below (1 = invoiced per trip).
// When it is off the panel simply does not render, which reads as "something
// failed to load" rather than "nothing is due here". The two off states carry
// opposite risk, so they get different notes:
//
//   - `invoicing_enabled === 0` — somebody deliberately put the partner on
//     monthly settlement. Informational only, hence plain muted text.
//   - absent / null / '' — the partner was never configured. The API treats
//     that as monthly as well (MissingDocumentsService: `isMonthly =
//     Number(p.invoicing_enabled) !== 1`, and monthly partners are excluded
//     from Invoicing's Missing documents and Needs final Számla via
//     `partner_id: { $nin: monthlyIds }`), so a partner that really IS
//     invoiced per trip has unbilled trips nobody can see. That one is worth
//     prompting on, so it gets the same `bg-warning-subtle` highlight
//     `DetailRow` uses.
//
// Read off the raw flag rather than changing what `isInvoicingEnabled` means —
// it answers one question (per-trip: yes/no) and both off states answer it the
// same way.
//
// NB: Bootstrap's `.alert` classes are deliberately NOT used here, even though
// the rest of the app leans on them. `AIOverviewWiring.test.jsx` asserts the
// whole modal holds no `.alert` when the AI briefing fails, and `Tab.Content`
// mounts every pane — so an alert on this tab would read as an error banner on
// all of them. The detail-card vocabulary is what this file uses anyway.
function PartnerInvoicingNote({ partner }) {
  const flag = partner?.invoicing_enabled;
  const configured = flag !== undefined && flag !== null && flag !== '';
  return (
    <DetailCard title="Per-Trip Invoicing" icon="fa-file-invoice">
      {configured ? (
        <div className="small text-muted" data-testid="partner-invoicing-note">
          This partner is settled monthly rather than per trip, so no invoice is
          raised against this booking. An unpaid booking here is the expected
          resting state — the month is invoiced as a whole, from Invoicing →
          Create document → Partner.
        </div>
      ) : (
        <div
          className="small bg-warning-subtle rounded p-2"
          data-testid="partner-invoicing-note"
        >
          <i className="fa fa-triangle-exclamation me-1 text-warning" />
          This partner record has no invoicing setting, so it is treated as
          monthly by default and no per-trip invoice is raised here. If this
          partner is in fact invoiced per trip, fill the setting in on the
          partner record — until then its bookings never appear in
          Invoicing&apos;s Missing documents or Needs final Számla.
        </div>
      )}
    </DetailCard>
  );
}

// The documents a booking already has, whoever it is invoiced to: the
// Invoicing lists' read path and document table, so its send / Számla /
// cancel are the same ones. Creating one stays with BillingoInvoicesPanel.
function BookingDocuments({ order, onChanged }) {
  const rows = useMemo(() => [{ booking_ids: [order._id] }], [order._id]);
  const docs = useBookingDocuments(rows);
  const documents = docs.documentsOf(rows[0]);
  return (
    <>
      <div className="small">{docs.noteFor(documents)}</div>
      <BillingoDocumentList
        documents={documents}
        finalBlocked={finalInvoiceBlockedReason([order])}
        defaultEmail={order.email_address || ''}
        onChanged={() => { docs.reload(); onChanged(); }}
      />
    </>
  );
}

function StatusBadge({ status }) {
  const statusColors = {
    Confirmed: 'success',
    Completed: 'primary',
    Cancelled: 'danger',
    Request: 'warning',
    Pending: 'warning',
    'Driver No-Show': 'danger',
    'Passenger No-Show': 'warning',
  };
  return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
}

function isPremiumCategory(categoryName) {
  if (!categoryName) return false;
  const premiumKeywords = ['business', 'premium', 'luxury', 'lux'];
  const lowerName = categoryName.toLowerCase();
  return premiumKeywords.some((keyword) => lowerName.includes(keyword));
}

function CategoryBadge({ categoryName }) {
  const isPremium = isPremiumCategory(categoryName);
  if (isPremium) {
    return (
      <span className="d-flex align-items-center gap-2">
        <Badge bg="warning" text="dark" className="d-flex align-items-center">
          <i className="fa fa-star me-1" />
          {categoryName}
        </Badge>
      </span>
    );
  }
  return <span>{categoryName || 'N/A'}</span>;
}

// Country code to flag emoji mapping (sorted by prefix length descending for matching)
const PHONE_COUNTRY_CODES = [
  // 4-digit codes first
  { prefix: '+1684', flag: '🇦🇸', country: 'American Samoa' },
  { prefix: '+1787', flag: '🇵🇷', country: 'Puerto Rico' },
  { prefix: '+1939', flag: '🇵🇷', country: 'Puerto Rico' },
  // 3-digit codes
  { prefix: '+420', flag: '🇨🇿', country: 'Czech Republic' },
  { prefix: '+421', flag: '🇸🇰', country: 'Slovakia' },
  { prefix: '+380', flag: '🇺🇦', country: 'Ukraine' },
  { prefix: '+375', flag: '🇧🇾', country: 'Belarus' },
  { prefix: '+370', flag: '🇱🇹', country: 'Lithuania' },
  { prefix: '+371', flag: '🇱🇻', country: 'Latvia' },
  { prefix: '+372', flag: '🇪🇪', country: 'Estonia' },
  { prefix: '+373', flag: '🇲🇩', country: 'Moldova' },
  { prefix: '+374', flag: '🇦🇲', country: 'Armenia' },
  { prefix: '+376', flag: '🇦🇩', country: 'Andorra' },
  { prefix: '+377', flag: '🇲🇨', country: 'Monaco' },
  { prefix: '+378', flag: '🇸🇲', country: 'San Marino' },
  { prefix: '+381', flag: '🇷🇸', country: 'Serbia' },
  { prefix: '+382', flag: '🇲🇪', country: 'Montenegro' },
  { prefix: '+383', flag: '🇽🇰', country: 'Kosovo' },
  { prefix: '+385', flag: '🇭🇷', country: 'Croatia' },
  { prefix: '+386', flag: '🇸🇮', country: 'Slovenia' },
  { prefix: '+387', flag: '🇧🇦', country: 'Bosnia' },
  { prefix: '+389', flag: '🇲🇰', country: 'North Macedonia' },
  { prefix: '+354', flag: '🇮🇸', country: 'Iceland' },
  { prefix: '+353', flag: '🇮🇪', country: 'Ireland' },
  { prefix: '+352', flag: '🇱🇺', country: 'Luxembourg' },
  { prefix: '+351', flag: '🇵🇹', country: 'Portugal' },
  { prefix: '+358', flag: '🇫🇮', country: 'Finland' },
  { prefix: '+359', flag: '🇧🇬', country: 'Bulgaria' },
  { prefix: '+357', flag: '🇨🇾', country: 'Cyprus' },
  { prefix: '+356', flag: '🇲🇹', country: 'Malta' },
  { prefix: '+355', flag: '🇦🇱', country: 'Albania' },
  // 2-digit codes
  { prefix: '+36', flag: '🇭🇺', country: 'Hungary' },
  { prefix: '+43', flag: '🇦🇹', country: 'Austria' },
  { prefix: '+49', flag: '🇩🇪', country: 'Germany' },
  { prefix: '+44', flag: '🇬🇧', country: 'United Kingdom' },
  { prefix: '+33', flag: '🇫🇷', country: 'France' },
  { prefix: '+39', flag: '🇮🇹', country: 'Italy' },
  { prefix: '+34', flag: '🇪🇸', country: 'Spain' },
  { prefix: '+31', flag: '🇳🇱', country: 'Netherlands' },
  { prefix: '+32', flag: '🇧🇪', country: 'Belgium' },
  { prefix: '+41', flag: '🇨🇭', country: 'Switzerland' },
  { prefix: '+45', flag: '🇩🇰', country: 'Denmark' },
  { prefix: '+46', flag: '🇸🇪', country: 'Sweden' },
  { prefix: '+47', flag: '🇳🇴', country: 'Norway' },
  { prefix: '+48', flag: '🇵🇱', country: 'Poland' },
  { prefix: '+40', flag: '🇷🇴', country: 'Romania' },
  { prefix: '+30', flag: '🇬🇷', country: 'Greece' },
  { prefix: '+90', flag: '🇹🇷', country: 'Turkey' },
  { prefix: '+91', flag: '🇮🇳', country: 'India' },
  { prefix: '+86', flag: '🇨🇳', country: 'China' },
  { prefix: '+81', flag: '🇯🇵', country: 'Japan' },
  { prefix: '+82', flag: '🇰🇷', country: 'South Korea' },
  { prefix: '+61', flag: '🇦🇺', country: 'Australia' },
  { prefix: '+64', flag: '🇳🇿', country: 'New Zealand' },
  { prefix: '+55', flag: '🇧🇷', country: 'Brazil' },
  { prefix: '+52', flag: '🇲🇽', country: 'Mexico' },
  { prefix: '+54', flag: '🇦🇷', country: 'Argentina' },
  { prefix: '+57', flag: '🇨🇴', country: 'Colombia' },
  { prefix: '+20', flag: '🇪🇬', country: 'Egypt' },
  { prefix: '+27', flag: '🇿🇦', country: 'South Africa' },
  { prefix: '+62', flag: '🇮🇩', country: 'Indonesia' },
  { prefix: '+63', flag: '🇵🇭', country: 'Philippines' },
  { prefix: '+66', flag: '🇹🇭', country: 'Thailand' },
  { prefix: '+65', flag: '🇸🇬', country: 'Singapore' },
  { prefix: '+60', flag: '🇲🇾', country: 'Malaysia' },
  { prefix: '+84', flag: '🇻🇳', country: 'Vietnam' },
  { prefix: '+98', flag: '🇮🇷', country: 'Iran' },
  { prefix: '+92', flag: '🇵🇰', country: 'Pakistan' },
  { prefix: '+93', flag: '🇦🇫', country: 'Afghanistan' },
  { prefix: '+94', flag: '🇱🇰', country: 'Sri Lanka' },
  { prefix: '+95', flag: '🇲🇲', country: 'Myanmar' },
  { prefix: '+972', flag: '🇮🇱', country: 'Israel' },
  { prefix: '+971', flag: '🇦🇪', country: 'UAE' },
  { prefix: '+966', flag: '🇸🇦', country: 'Saudi Arabia' },
  { prefix: '+974', flag: '🇶🇦', country: 'Qatar' },
  { prefix: '+973', flag: '🇧🇭', country: 'Bahrain' },
  { prefix: '+968', flag: '🇴🇲', country: 'Oman' },
  { prefix: '+965', flag: '🇰🇼', country: 'Kuwait' },
  { prefix: '+962', flag: '🇯🇴', country: 'Jordan' },
  { prefix: '+961', flag: '🇱🇧', country: 'Lebanon' },
  // 1-digit code (last as it's the shortest)
  { prefix: '+1', flag: '🇺🇸', country: 'USA/Canada' },
  { prefix: '+7', flag: '🇷🇺', country: 'Russia' },
];

function getCountryFromPhone(phoneNumber) {
  if (!phoneNumber) return null;
  // Normalize: remove spaces, dashes, parentheses
  const normalized = phoneNumber.replace(/[\s\-()]/g, '');
  // Ensure it starts with +
  const withPlus = normalized.startsWith('+') ? normalized : `+${normalized}`;

  // Find matching country code (sorted by prefix length, so longer matches first)
  return PHONE_COUNTRY_CODES.find((entry) => withPlus.startsWith(entry.prefix)) || null;
}

function PhoneWithFlag({ phoneNumber }) {
  if (!phoneNumber) return <span>N/A</span>;
  const country = getCountryFromPhone(phoneNumber);
  return (
    <span>
      {phoneNumber}
      {country && (
        <small className="text-muted ms-2">
          {country.flag}
          {' '}
          {country.country}
        </small>
      )}
    </span>
  );
}

/**
 * One booking, every side of it, in tabs. Besides the booking and `initialTab`:
 *   - `onChanged()`     after anything here wrote to the booking's money or
 *                       documents — a list behind the modal reloads on close
 *   - `siblings`        `{ onPrevious, onNext, position }` when opened from a
 *                       list: ‹ Previous / Next › in the header swap the
 *                       booking without closing (a null handler = the end)
 *   - `ledgerReadOnly`  the Order Ledger tab without its form and actions
 *   - `lineItem`        `{ prefill, prefillNote }` to seed the Order Ledger
 *                       tab's adjustment form with (see tripClaimLineItems)
 */
function OrderDetailsModal({
  order: initialOrder, show, handleClose, initialTab = 'overview',
  onChanged = () => {}, siblings = null, ledgerReadOnly = false, lineItem: initialLineItem = null,
}) {
  // Stripe billing overwrite — fetched lazily when the user asks.
  const [stripeBilling, setStripeBilling] = useState(null);
  const [stripeBillingBusy, setStripeBillingBusy] = useState(false);
  const [stripeBillingError, setStripeBillingError] = useState('');
  const [showBillingOverwrite, setShowBillingOverwrite] = useState(false);

  const [order, setOrder] = useState(initialOrder);

  // Pull the cardholder's billing address on demand. Not fetched on open:
  // it applies to a small minority of bookings and would be a wasted call
  // on every order view.
  const loadStripeBilling = async () => {
    setStripeBillingBusy(true);
    setStripeBillingError('');
    try {
      const data = await BookingService.fetchStripeBilling(order.booking_reference);
      if (!data?.billing_details) {
        setStripeBillingError('No Stripe card payment with billing details is linked to this booking.');
        return;
      }
      // 847 of 869 bookings with a Stripe intent already match. Say so
      // rather than opening a dialog with nothing to change in it.
      if (!billingDiffers(order.billing || {}, data.billing_details)) {
        setStripeBillingError('The Stripe billing details are identical to the ones already on this booking.');
        return;
      }
      setStripeBilling(data.billing_details);
      setShowBillingOverwrite(true);
    } catch (err) {
      setStripeBillingError(err.response?.data?.message || err.message || 'Could not load the Stripe billing details.');
    } finally {
      setStripeBillingBusy(false);
    }
  };

  // One booking at a time, always confirmed — see the note in
  // StripeBillingOverwriteModal on why this is never a bulk action.
  const confirmBillingOverwrite = async (billing) => {
    setStripeBillingBusy(true);
    setStripeBillingError('');
    try {
      await BookingService.updateBookingFields(order._id, { billing });
      setShowBillingOverwrite(false);
      setStripeBilling(null);
      // Reflect the change in the open modal without a refetch.
      setOrder((prev) => ({ ...prev, billing }));
      onChanged();
    } catch (err) {
      setStripeBillingError(err.response?.data?.message || err.message || 'Could not save the billing details.');
    } finally {
      setStripeBillingBusy(false);
    }
  };

  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const [linkedBookings, setLinkedBookings] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Trip claims — receipts and overtime waiting for review — shared by the
  // banner and the Driver events tab. The receipts are fetched once the tab
  // is opened, or up front when the list already counted some.
  const canReviewClaims = canReviewTripClaims();
  const claims = useBookingClaims(order, {
    enabled: Boolean(show && canReviewClaims
      && (activeTab === 'driver-events' || tripClaimsOf(order).receipts > 0)),
  });
  // "Create line item" on an approved claim: the Order Ledger tab with its
  // form pre-filled (nothing is saved until the admin saves it there). `seq`
  // remounts the tab, so a second claim re-seeds a form already open.
  const [lineItem, setLineItem] = useState(initialLineItem && { ...initialLineItem, seq: 1 });
  const openLineItem = (claim) => {
    setLineItem((prev) => ({ ...lineItemPrefillFor(order, claim), seq: (prev?.seq ?? 0) + 1 }));
    setActiveTab('ledger');
  };
  // Asking the assistant from the header: its card sits over this modal, and
  // the modal must stop pulling the focus back out of it.
  const [chatting, setChatting] = useState(false);
  // The passenger-message composer: { show, channel, autoDraft }. ONE
  // composer with two doors — the abandoned-basket panel below, and the
  // passenger's own phone number in the Passenger Information card — and
  // `autoDraft` is the only thing that differs between them. The basket has
  // no operational line to type: the whole message is the persuasion, and
  // nobody opens that panel for another reason, so it asks for a draft on
  // open. Tapping a phone number does not: somebody who did that already
  // knows what they want to say.
  const [messaging, setMessaging] = useState({
    show: false, channel: 'whatsapp', autoDraft: false,
  });
  // Reminder form state
  const [newReminderTitle, setNewReminderTitle] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderNotify, setNewReminderNotify] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);
  // Payment history (Financial tab, and the ledger's paid / outstanding) —
  // fetched each time the modal opens.
  const paymentsState = useBookingPayments(order?._id, { enabled: show });
  const { payments } = paymentsState;
  const invoicePdf = useInvoicePdf();

  // The booking whose fresh copy is on screen. Keyed on the id rather than on
  // "fetched once": ‹ Previous / Next › hand in another booking without
  // closing, and its details must not render from the last one's.
  const bookingId = initialOrder?._id;
  const [loadedId, setLoadedId] = useState(null);
  const isLoading = Boolean(show && bookingId && loadedId !== bookingId);

  useEffect(() => {
    if (!show || !bookingId || loadedId === bookingId) return undefined;
    // Another booking than the one on screen: its header shows at once, and
    // nothing begun on the last one carries over.
    if (loadedId) {
      setOrder(initialOrder);
      setLineItem(null);
      setStripeBillingError('');
    }
    let live = true;
    (async () => {
      let freshOrder = null;
      try {
        freshOrder = await BookingService.getBookingById(bookingId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching booking:', err);
      }
      if (!live) return;
      // Fall back to the booking handed in if the fetch fails — which may be
      // only what a list knew of it, hence the `?.` on its nested fields.
      setOrder(freshOrder || initialOrder);
      setLoadedId(bookingId);
    })();
    return () => { live = false; };
  }, [show, bookingId]);

  // A ledger write can change the booking's totals; re-read it quietly, so
  // the header and the Pricing card follow without a spinner.
  const ledgerChanged = () => {
    setLineItem((prev) => prev && { seq: prev.seq });
    BookingService.getBookingById(order._id)
      .then((fresh) => fresh && setOrder((prev) => (prev?._id === fresh._id ? fresh : prev)))
      .catch(() => {});
    onChanged();
  };

  // Fetch linked bookings when order has linked_booking_ids
  useEffect(() => {
    const fetchLinkedBookings = async () => {
      if (!order?.linked_booking_ids || order.linked_booking_ids.length === 0) {
        setLinkedBookings([]);
        return;
      }

      setLoadingLinked(true);
      try {
        const response = await get('/bookings', {
          filters: { _id: { $in: order.linked_booking_ids } },
        });
        const documents = response.data?.data?.documents || [];
        setLinkedBookings(documents);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching linked bookings:', err);
      } finally {
        setLoadingLinked(false);
      }
    };

    fetchLinkedBookings();
  }, [order?.linked_booking_ids]);

  // Has at least one inbound completed payment been recorded for this
  // booking? Used to (a) hide the "Mark Wire Received" button when the
  // booking is already settled and (b) show a "(Payment Recorded)"
  // muted note next to the legacy Paid badge.
  const hasInboundCompletedPayment = payments.some(
    (p) => p?.direction === 'in' && p?.status === 'completed',
  );

  // Has any inbound payment been fully refunded? We detect this from
  // the local ledger rather than asking the backend so the UI updates
  // optimistically after a refund without a re-fetch. "Refunded" here
  // means an inbound payment whose status is 'refunded' (set either by
  // the backend on a full refund or optimistically by handleRefunded).
  const hasRefundedInbound = payments.some(
    (p) => p?.direction === 'in' && p?.status === 'refunded',
  );

  // Partially refunded? An outbound payment with parent_payment_id whose
  // gross_amount is less than its parent's. We pair offset rows with
  // their originals via parent_payment_id and compare gross. This is the
  // best we can do without a backend-returned `was_full_refund` flag.
  const hasPartialRefund = payments.some((p) => {
    if (p?.direction !== 'out' || !p?.parent_payment_id) return false;
    const parent = payments.find((q) => q?._id === p.parent_payment_id);
    if (!parent) return false;
    return Number(p.gross_amount) < Number(parent.gross_amount);
  });

  // After a refund: flip Booking.payment.paid back to false when the refund
  // was full AND the original was an inbound booking payment AND no other
  // inbound completed payment still exists. Mirrors the backend rule
  // documented in the locked contract. `payments` is still the list from
  // before the refund here.
  const handleRefunded = (offset, originalPaymentId, { original, wasFullRefund }) => {
    if (wasFullRefund
      && original?.direction === 'in'
      && original?.counterparty?.kind === 'booking') {
      const otherInboundCompletedExists = payments.some(
        (p) => p?._id !== originalPaymentId
          && p?.direction === 'in'
          && p?.status === 'completed',
      );
      if (!otherInboundCompletedExists) {
        setOrder((prev) => ({
          ...prev,
          payment: { ...(prev?.payment || {}), paid: false },
        }));
      }
    }
  };

  // Open on `initialTab`; forget the booking and the tab on close.
  useEffect(() => {
    if (show) {
      setActiveTab(initialTab);
    } else {
      setLoadedId(null);
      setActiveTab('overview');
    }
  }, [show, initialTab]);

  // `initialTab` arrives from outside — `/orders?booking=…&tab=…`, the bell,
  // a trip-claim link — so it can name a tab this booking does not have, and
  // Previous / Next can bring a booking without the tab that is open. On an
  // expense that would show a pane with no tab selected, which reads as an
  // empty modal; fall back to Overview instead.
  useEffect(() => {
    if (!isTabAvailable(order, activeTab)) setActiveTab('overview');
  }, [order?.booking_type, activeTab]);

  // Check if we have valid coordinates for map
  const hasValidMapCoords = order?.destination_from?.latitude
    && order?.destination_from?.longitude
    && order?.destination_to?.latitude
    && order?.destination_to?.longitude;

  if (!order) return null;

  const typeInfo = BOOKING_TYPE_LABELS[order.booking_type] || BOOKING_TYPE_LABELS.transfer;
  /**
   * An expense keeps the tabs that say WHAT IT COST and WHO WE PAID —
   * overview, operators, financial, other, version history — and loses the
   * ones that describe a journey. See `EXPENSE_HIDDEN_TABS`.
   */
  const isExpense = isExpenseBooking(order);
  // Documents are CREATED here only for a partner invoiced per trip.
  const perTripInvoicing = Boolean(order.partner_id && isInvoicingEnabled(order.partner));

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        className="order-details-modal"
        enforceFocus={!chatting}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center flex-wrap gap-2 flex-grow-1">
            <span>
              <i className="fa fa-file-invoice me-2 text-primary" />
              {order.booking_reference || 'Order Details'}
            </span>
            <Badge bg={typeInfo.color}>
              <i className={`fa ${typeInfo.icon} me-1`} />
              {typeInfo.label}
            </Badge>
            <StatusBadge status={order.status} />
            <span className="ms-auto d-flex align-items-center gap-1 fs-6 fw-normal">
              <AskAIButton
                refs={[order.booking_reference || order._id]}
                onAsk={() => setChatting(true)}
              />
              {siblings && (
                <>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={siblings.onPrevious}
                    disabled={!siblings.onPrevious}
                    aria-label="Previous booking"
                  >
                    <i className="fa fa-chevron-left" aria-hidden="true" />
                    <span className="d-none d-sm-inline ms-1">Previous</span>
                  </Button>
                  {siblings.position && (
                    <small className="text-muted text-nowrap">{siblings.position}</small>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={siblings.onNext}
                    disabled={!siblings.onNext}
                    aria-label="Next booking"
                  >
                    <span className="d-none d-sm-inline me-1">Next</span>
                    <i className="fa fa-chevron-right" aria-hidden="true" />
                  </Button>
                </>
              )}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {/*
            The AI overview sits ABOVE the loading gate below on purpose.
            Mounted here it fires its request the moment the modal opens —
            not after the booking has been re-fetched — and the details
            render underneath it as soon as they arrive. Generation takes a
            few seconds server-side and is never awaited, so nothing here can
            delay the details the user actually came for.

            The booking id is the Mongo `_id` with the reference as the
            fallback; the endpoint takes either. No id means no request:
            that, structurally, is the "existing bookings only" rule.
          */}
          <AIOverviewPanel
            bookingId={initialOrder?._id || initialOrder?.booking_reference}
            enabled={show}
            /*
              Time to pickup is rendered from the booking the modal already
              has, NOT from the briefing: a cached briefing saying "travels
              in 36 hours" would be wrong by the next morning and no
              fingerprint change would catch it.
            */
            pickupAt={initialOrder?.date_datetime}
          />
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Loading booking details...</p>
            </div>
          ) : (
            <div className="order-details-container">
              {/* Trip claims waiting for review — reviewed right here, or in
                  the Driver events tab (which shows them itself). */}
              {canReviewClaims && activeTab !== 'driver-events' && (
                <TripClaimsBanner
                  claims={claims}
                  onOpenDriverEvents={() => setActiveTab('driver-events')}
                  onCreateLineItem={openLineItem}
                />
              )}

              {/* Quick Summary Header */}
              <div className="quick-summary mb-4 p-3 bg-light rounded-3">
                <div className="row g-3">
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Customer</small>
                    <strong>{order.full_name}</strong>
                  </div>
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Date & Time</small>
                    <strong>{formatBookingDate(order, 'date_datetime')}</strong>
                  </div>
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Passengers</small>
                    <strong>{order.passengers?.amount}</strong>
                  </div>
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-block">Total Price</small>
                    <strong className="text-success">
                      {thousandSeparator(order.total_price ?? order.price)}
                      {' '}
                      {order.currency || 'EUR'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Abandoned-basket follow-up for Request status */}
              {order.status === 'Request' && (
              <div className="mb-4 p-3 bg-warning-subtle border border-warning rounded-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <i className="fa fa-lightbulb text-warning me-2" />
                    <strong>Abandoned Basket</strong>
                    <span className="text-muted ms-2">Write the customer a follow-up, or draft it with AI</span>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setMessaging({ show: true, channel: 'email', autoDraft: true })}
                      disabled={!order.email_address}
                      title={!order.email_address ? 'No email on this booking' : undefined}
                    >
                      <i className="fa fa-envelope me-1" />
                      Send Email
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => setMessaging({
                        show: true, channel: 'whatsapp', autoDraft: true,
                      })}
                      disabled={!order.phone_number}
                      title={!order.phone_number ? 'No phone on this booking' : undefined}
                    >
                      <i className="fab fa-whatsapp me-1" />
                      Send WhatsApp
                    </Button>
                  </div>
                </div>
                {/* Already-contacted note so agents don't double-message the
                    customer. Newest first. */}
                {order.follow_ups?.length > 0 && (
                  <div className="mt-2 pt-2 border-top border-warning-subtle">
                    {[...order.follow_ups].reverse().map((f) => (
                      <div
                        // eslint-disable-next-line no-underscore-dangle
                        key={f._id || `${f.channel}-${f.sent_at}`}
                        className="small text-muted"
                      >
                        <i className={`${f.channel === 'whatsapp' ? 'fab fa-whatsapp' : 'fa fa-envelope'} me-1`} />
                        Messaged by
                        {' '}
                        <strong>{f.sent_by || 'unknown'}</strong>
                        {f.sent_at && (
                          <>
                            {' '}
                            on
                            {' '}
                            {format(f.sent_at)}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Tabbed Navigation */}
              <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">
                      <i className="fa fa-home" />
                      <span className="tab-text d-none d-sm-inline ms-1">Overview</span>
                    </Nav.Link>
                  </Nav.Item>
                  {isTabAvailable(order, 'route') && (
                  <Nav.Item>
                    <Nav.Link eventKey="route">
                      <i className="fa fa-route" />
                      <span className="tab-text d-none d-sm-inline ms-1">Route</span>
                      {order.programme?.stops?.length > 0 && (
                      <Badge bg="secondary" className="ms-1">{order.programme.stops.length}</Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="operators">
                      <i className="fa fa-user-tie" />
                      <span className="tab-text d-none d-sm-inline ms-1">Operators</span>
                    </Nav.Link>
                  </Nav.Item>
                  {isTabAvailable(order, 'driver-events') && (
                  <Nav.Item>
                    <Nav.Link eventKey="driver-events" data-testid="driver-events-tab-link">
                      <i className="fa fa-car-side" />
                      <span className="tab-text d-none d-sm-inline ms-1">Driver events</span>
                      {claims.counts.total > 0 && (
                        <Badge bg="warning" text="dark" className="ms-1" data-testid="driver-events-tab-count">
                          {claims.counts.total}
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  {isTabAvailable(order, 'partner') && (
                  <Nav.Item>
                    <Nav.Link eventKey="partner">
                      <i className="fa fa-handshake" />
                      <span className="tab-text d-none d-sm-inline ms-1">Partner</span>
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="financial">
                      <i className="fa fa-money-bill" />
                      <span className="tab-text d-none d-sm-inline ms-1">Financial</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="ledger">
                      <i className="fa fa-list" />
                      <span className="tab-text d-none d-sm-inline ms-1">Order Ledger</span>
                    </Nav.Link>
                  </Nav.Item>
                  {/* Extras tab — shown only when the booking has any
                    services attached. Read-only view of the snapshotted
                    catalog items so admins can confirm what was added
                    without leaving OrderDetails for the order ledger. */}
                  {isTabAvailable(order, 'extras')
                    && Array.isArray(order.services) && order.services.length > 0 && (
                    <Nav.Item>
                      <Nav.Link eventKey="extras">
                        <i className="fa fa-gift" />
                        <span className="tab-text d-none d-sm-inline ms-1">
                          Extras
                          {' '}
                          <Badge bg="light" text="dark">{order.services.length}</Badge>
                        </span>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {(order.is_event_booking || order.event_name || order.visitor_tracking) && (
                  <Nav.Item>
                    <Nav.Link eventKey="other">
                      <i className="fa fa-ellipsis-h" />
                      <span className="tab-text d-none d-sm-inline ms-1">Other</span>
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  {isTabAvailable(order, 'reminders') && (
                  <Nav.Item>
                    <Nav.Link eventKey="reminders">
                      <i className="fa fa-bell me-1" />
                      Reminders
                      {(() => {
                        const now = new Date();
                        const overdue = (order.notifications || []).filter(
                          (n) => !n.is_completed && new Date(n.due_date) <= now,
                        ).length;
                        return overdue > 0 ? (
                          <Badge bg="warning" text="dark" className="ms-1">{overdue}</Badge>
                        ) : null;
                      })()}
                    </Nav.Link>
                  </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="version-history">
                      <i className="fa fa-history" />
                      <span className="tab-text d-none d-sm-inline ms-1">Version History</span>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  {/* OVERVIEW TAB */}
                  <Tab.Pane eventKey="overview">
                    <div className="row g-3">
                      {/* Passenger Information */}
                      <div className="col-md-6">
                        <DetailCard
                          title={isExpense ? 'Expense' : 'Passenger Information'}
                          icon={isExpense ? 'fa-receipt' : 'fa-user'}
                        >
                          {/* `full_name` IS the description on an expense —
                              the API reuses the field so that every list,
                              search and CSV renders it with no change. */}
                          <DetailRow label={isExpense ? 'Description' : 'Name'} value={order.full_name} />
                          {!isExpense && (
                          <DetailRow
                            label="Email"
                            value={(
                              <>
                                {order.email_address}
                                {/* The MIRROR of the phone row's control,
                                    one line up: see the address, write to
                                    it. Composed at the call site for the
                                    same reason — this is an action on the
                                    booking, not part of rendering an
                                    address — and it is the same component
                                    with its channel set, so the two gates
                                    (the passenger's own field, and the
                                    booking being a journey) are not
                                    restated here.

                                    No `autoDraft`: that belongs to the
                                    abandoned-basket panel and nowhere
                                    else. Somebody who clicked an address
                                    has a line in mind. */}
                                <PassengerMessageButton
                                  order={order}
                                  channel="email"
                                  onOpen={() => setMessaging({
                                    show: true, channel: 'email', autoDraft: false,
                                  })}
                                  className="ms-2 align-baseline"
                                />
                                {order.skip_review_email && (
                                <Badge bg="danger" className="ms-2" style={{ fontSize: '0.7em' }}>
                                  <i className="fa fa-ban me-1" />
                                  No review email
                                </Badge>
                                )}
                              </>
                          )}
                          />
                          )}
                          {!isExpense && (
                          <DetailRow
                            label="Phone"
                            value={(
                              <>
                                <PhoneWithFlag phoneNumber={order.phone_number} />
                                {/* Composed at the call site rather than
                                    folded into PhoneWithFlag: that one
                                    FORMATS a number (and is used only here),
                                    while this is an action on the booking. */}
                                {/* The GENERIC door. Manual drafting on
                                    purpose: `autoDraft` belongs to the
                                    abandoned-basket panel and nowhere else. */}
                                <PassengerMessageButton
                                  order={order}
                                  onOpen={() => setMessaging({
                                    show: true, channel: 'whatsapp', autoDraft: false,
                                  })}
                                  className="ms-2 align-baseline"
                                />
                              </>
                            )}
                          />
                          )}
                          {order.booking_type !== 'experience' && !isExpense && (
                          <DetailRow label="Flight Number" value={order.flight_number} />
                          )}
                        </DetailCard>
                      </div>

                      {/* Passengers Count — nobody travels on a cost record,
                          and the card would read a flat "Total 0". */}
                      {!isExpense && (
                      <div className="col-md-6">
                        <DetailCard title="Passengers" icon="fa-users">
                          <DetailRow label="Adults" value={order.passengers?.adults} />
                          {order.booking_type === 'experience' ? (
                            <DetailRow label="Children" value={order.passengers?.children || 0} />
                          ) : (
                            <>
                              <DetailRow label="Convertible Seats" value={order.passengers?.convertible_seat} />
                              <DetailRow label="Booster Seats" value={order.passengers?.booster_seat} />
                              <DetailRow label="Infant Carriers" value={order.passengers?.infant_carrier} />
                            </>
                          )}
                          <DetailRow label="Total" value={order.passengers?.amount} highlight />
                        </DetailCard>
                      </div>
                      )}

                      {/* Travel Information — an expense keeps only the date,
                          since the addresses are the fields the fake cost
                          bookings were abusing in the first place. */}
                      <div className="col-md-6">
                        <DetailCard
                          title={(() => {
                            if (isExpense) return 'When';
                            return order.booking_type === 'experience' ? 'Experience Location' : 'Travel Information';
                          })()}
                          icon={isExpense ? 'fa-calendar-day' : 'fa-map-marker-alt'}
                        >
                          {!isExpense && (
                          <>
                            <DetailRow
                              label={order.booking_type === 'experience' ? 'Starts From' : 'Pickup'}
                              value={order.destination_from?.address}
                            />
                            <DetailRow
                              label={order.booking_type === 'experience' ? 'Ends At' : 'Drop-off'}
                              value={order.destination_to?.address}
                            />
                          </>
                          )}
                          <DetailRow label="Date" value={formatBookingDate(order, 'date_datetime')} />
                          {order.return_date_datetime && (
                          <DetailRow
                            label="Return (Legacy)"
                            value={formatBookingDate(order, 'return_date_datetime')}
                          />
                          )}
                        </DetailCard>
                      </div>

                      {/* Vehicle & Order Details */}
                      <div className="col-md-6">
                        <DetailCard title="Order Details" icon={order.booking_type === 'experience' ? 'fa-info-circle' : 'fa-car'}>
                          <DetailRow label="Status" value={<StatusBadge status={order.status} />} />
                          <DetailRow label="Reference" value={order.booking_reference} />
                          {order.booking_type !== 'experience' && !isExpense && (
                          <>
                            <DetailRow label="Vehicle Category" value={<CategoryBadge categoryName={order.category?.name} />} />
                            <DetailRow label="Capacity" value={order.category?.capacity} />
                            <DetailRow label="Max Luggage" value={order.category?.max_luggage} />
                          </>
                          )}
                          {/* The Partner tab is hidden on an expense, but
                              `partner_id` is REQUIRED on one — it is who
                              bears the cost — so it has to be readable
                              somewhere. Here, not nowhere. */}
                          {isExpense && (
                          <DetailRow label="Partner" value={order.partner?.name} />
                          )}
                          <DetailRow label="Timezone" value={order.timezone} />
                        </DetailCard>
                      </div>

                      {/* Comments */}
                      <div className="col-12">
                        <DetailCard title="Comments" icon="fa-comment">
                          <div className="row">
                            <div className="col-md-6">
                              <DetailRow label="Customer Comments" value={order.comments || 'None'} />
                            </div>
                            <div className="col-md-6">
                              <DetailRow label="Admin Comments" value={order.admin_comments || 'None'} />
                            </div>
                          </div>
                        </DetailCard>
                      </div>

                      {/* Linked Bookings */}
                      {order.linked_booking_ids?.length > 0 && (
                      <div className="col-12">
                        <DetailCard title={`Linked Bookings (${order.linked_booking_ids.length})`} icon="fa-link">
                          {loadingLinked ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" />
                              <span className="ms-2">Loading linked bookings...</span>
                            </div>
                          ) : (
                            <Table responsive striped hover size="sm" className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Passenger</th>
                                  <th>Time</th>
                                  <th>From</th>
                                  <th>To</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {linkedBookings.map((booking) => (
                                  <tr key={booking._id}>
                                    <td>
                                      <div className="fw-bold">{booking.full_name || '-'}</div>
                                      <small className="text-muted">{booking.booking_reference}</small>
                                    </td>
                                    <td>
                                      <div className="fw-bold">{formatBookingTimeOnly(booking)}</div>
                                      <small className="text-muted">{formatBookingDateOnly(booking)}</small>
                                    </td>
                                    <td>
                                      <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                                        {booking.destination_from?.address || '-'}
                                      </small>
                                    </td>
                                    <td>
                                      <small className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                                        {booking.destination_to?.address || '-'}
                                      </small>
                                    </td>
                                    <td><StatusBadge status={booking.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </DetailCard>
                      </div>
                      )}
                    </div>
                  </Tab.Pane>

                  {/* ROUTE TAB */}
                  <Tab.Pane eventKey="route">
                    <div className="row g-3">
                      {/* Route Map Panel */}
                      {hasValidMapCoords && (
                      <div className="col-12">
                        <MapRoutePanel
                          fromCoords={{
                            lat: order.destination_from.latitude,
                            lon: order.destination_from.longitude,
                          }}
                          toCoords={{
                            lat: order.destination_to.latitude,
                            lon: order.destination_to.longitude,
                          }}
                          fromAddress={order.destination_from.address}
                          toAddress={order.destination_to.address}
                          stops={order.programme?.stops || []}
                          isCollapsed={isMapCollapsed}
                          onToggleCollapse={() => setIsMapCollapsed(!isMapCollapsed)}
                        />
                      </div>
                      )}

                      {/* Programme Information */}
                      {order.programme?.enabled && (
                      <div className="col-12">
                        <DetailCard title="Programme Information" icon="fa-calendar-alt">
                          <DetailRow label="Programme Name" value={order.programme?.name} />
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <DetailRow label="Total Duration" value={order.programme?.total_hours ? `${order.programme.total_hours} hours` : 'N/A'} />
                            </div>
                            <div className="col-md-6">
                              <DetailRow label="Stops" value={order.programme?.stops?.length || 0} />
                            </div>
                          </div>
                          {order.programme?.itinerary && (
                            <div className="p-3 bg-light rounded mb-3">
                              <small className="text-muted d-block mb-1">Itinerary Description:</small>
                              <p className="mb-0">{order.programme.itinerary}</p>
                            </div>
                          )}
                          {/* Structured Itinerary Stops */}
                          {order.programme?.stops?.length > 0 && (
                            <div className="table-responsive">
                              <table className="table table-sm table-striped table-hover mb-0">
                                <thead className="table-dark">
                                  <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th style={{ width: '100px' }}>Type</th>
                                    <th>Address</th>
                                    <th style={{ width: '100px' }}>Arrival</th>
                                    <th style={{ width: '100px' }}>Departure</th>
                                    <th style={{ width: '80px' }}>Duration</th>
                                    <th>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {order.programme.stops.map((stop, idx) => {
                                    const stopInfo = STOP_TYPE_LABELS[stop.stop_type]
                                      || { label: stop.stop_type, color: 'secondary' };
                                    return (
                                      <tr key={`stop-${stop.order || idx}`}>
                                        <td className="text-center fw-bold">{stop.order || idx + 1}</td>
                                        <td>
                                          <Badge bg={stopInfo.color}>{stopInfo.label}</Badge>
                                        </td>
                                        <td>{stop.address || 'N/A'}</td>
                                        <td>{stop.arrival_time ? format(stop.arrival_time) : '-'}</td>
                                        <td>{stop.departure_time ? format(stop.departure_time) : '-'}</td>
                                        <td>{stop.duration_minutes ? `${stop.duration_minutes} min` : '-'}</td>
                                        <td><small className="text-muted">{stop.notes || '-'}</small></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </DetailCard>
                      </div>
                      )}

                      {/* Simple route info if no programme */}
                      {!order.programme?.enabled && (
                      <div className="col-12">
                        <DetailCard title="Route" icon="fa-route">
                          <div className="row">
                            <div className="col-md-6">
                              <DetailRow
                                label={order.booking_type === 'experience' ? 'Start' : 'Pickup'}
                                value={order.destination_from?.address}
                              />
                            </div>
                            <div className="col-md-6">
                              <DetailRow
                                label={order.booking_type === 'experience' ? 'End' : 'Drop-off'}
                                value={order.destination_to?.address}
                              />
                            </div>
                          </div>
                        </DetailCard>
                      </div>
                      )}

                      {/* Voucher Information */}
                      {(order.important_information || order.cancellation_policy) && (
                      <div className="col-12">
                        <DetailCard title="Voucher Information" icon="fa-file-alt">
                          {order.important_information && (
                            <DetailRow
                              label="Important Information"
                              value={(
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                  {order.important_information}
                                </div>
                              )}
                            />
                          )}
                          {order.cancellation_policy && (
                            <DetailRow
                              label="Cancellation Policy"
                              value={order.cancellation_policy}
                            />
                          )}
                        </DetailCard>
                      </div>
                      )}
                    </div>
                  </Tab.Pane>

                  {/* OPERATORS TAB */}
                  <Tab.Pane eventKey="operators">
                    <div className="row g-3">
                      {/* Operators Section - Multi-operator support */}
                      {order.operators?.length > 0 ? (
                        <div className="col-12">
                          <DetailCard title={`Assigned Operators (${order.operators.length})`} icon="fa-users-cog">
                            <div className="row g-3">
                              {order.operators.map((op, idx) => {
                                const roleInfo = operatorRoleBadge(op.role, op.operator);
                                const opKey = `${op.role}-${op.operator_id || idx}`;
                                return (
                                  <div key={opKey} className="col-md-6">
                                    <div className="border rounded p-3 h-100 bg-light">
                                      <div className="d-flex align-items-center mb-3">
                                        <Badge bg={roleInfo.color} className="me-2">
                                          <i className={`fa ${roleInfo.icon} me-1`} />
                                          {roleInfo.label}
                                        </Badge>
                                        <strong>{op.operator?.name || 'Unassigned'}</strong>
                                      </div>
                                      <div className="row g-2">
                                        {op.role === 'driver' && (
                                        <div className="col-6">
                                          <small className="text-muted d-block">Driver</small>
                                          <span>{op.driver ? `${op.driver.full_name || ''} ${op.driver.phone_number || ''}`.trim() || 'Assigned' : 'Not assigned'}</span>
                                        </div>
                                        )}
                                        <div className="col-6">
                                          <small className="text-muted d-block">Price</small>
                                          <span className="fw-bold text-success">
                                            {formatOperatorEntryPrice(op) || 'N/A'}
                                          </span>
                                        </div>
                                        {/* Booked against this operator AFTER its fee was
                                            agreed, and kept as its own figure — never added
                                            into Price or the total. See Utils/operatorCost.
                                            Omitted when there is none, which is most
                                            bookings. */}
                                        {formatOperatorAdjustments(op) && (
                                        <div className="col-6">
                                          <small className="text-muted d-block">Adjustments</small>
                                          <span className="fw-bold text-success">
                                            {formatOperatorAdjustments(op)}
                                          </span>
                                        </div>
                                        )}
                                        {op.role === 'driver' && (
                                        <div className="col-12">
                                          <small className="text-muted d-block">Vehicle</small>
                                          <span>{op.car ? `${op.car.type || ''} - ${op.car.plate_number || ''} - ${op.car.color || ''}`.replace(/ - $/g, '').replace(/^- /g, '') : 'Not assigned'}</span>
                                        </div>
                                        )}
                                        {formatOperatorEntryCash(op) && (
                                        <div className="col-6">
                                          <small className="text-muted d-block">Cash from Client</small>
                                          <span>{formatOperatorEntryCash(op)}</span>
                                        </div>
                                        )}
                                        {op.comments && (
                                        <div className="col-12">
                                          <small className="text-muted d-block">Comments</small>
                                          <span className="fst-italic">{op.comments}</span>
                                        </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Total operators price, per currency — see
                                Utils/operatorCost. Dropped entirely when no
                                entry has a resolved cost, rather than printed
                                as a "0" in a currency we had to guess. */}
                            {formatOperatorPriceTotals(order.operators) && (
                            <div className="mt-3 pt-3 border-top">
                              <DetailRow
                                label="Total Operators Price"
                                value={formatOperatorPriceTotals(order.operators)}
                                highlight
                              />
                            </div>
                            )}
                            {/* Show cancellation compensation at booking level */}
                            {(order.operator_cancellation_price > 0) && (
                            <div className="mt-3 pt-3 border-top">
                              <DetailRow
                                label="Operator Cancellation Compensation"
                                value={`${order.operator_cancellation_price ?? 0} ${order.operator_cancellation_currency || 'EUR'}`}
                              />
                            </div>
                            )}
                          </DetailCard>
                        </div>
                      ) : order.operator ? (
                      /* Legacy single operator display */
                        <div className="col-md-6">
                          <DetailCard title="Operator" icon="fa-user-tie">
                            <DetailRow label="Operator" value={order.operator?.name} />
                            <DetailRow label="Operator Booking #" value={order.operator_booking_reference} />
                            <DetailRow
                              label="Price"
                              value={`${thousandSeparator(order.operator_price)} ${order.operator_currency}`}
                            />
                            <DetailRow
                              label="Driver"
                              value={`${order.driver?.full_name || ''} ${order.driver?.phone_number || ''}`.trim() || 'Not assigned'}
                            />
                            <DetailRow
                              label="Car"
                              value={order.car ? `${order.car.type || ''} - ${order.car.plate_number || ''} - ${order.car.color || ''}` : 'Not assigned'}
                            />
                            {(order.operator_cash_from_client > 0) && (
                            <DetailRow
                              label="Cash from Client"
                              value={`${thousandSeparator(order.operator_cash_from_client)} ${order.operator_cash_from_client_currency || 'EUR'}`}
                            />
                            )}
                            <DetailRow label="Comments" value={order.operator_comments} />
                            <DetailRow
                              label="Cancellation Compensation"
                              value={`${order.operator_cancellation_price ?? 0} ${order.operator_cancellation_currency || 'EUR'}`}
                            />
                          </DetailCard>
                        </div>
                      ) : (
                        <div className="col-12">
                          <DetailCard title="Operator" icon="fa-user-tie">
                            <div className="text-center py-4 text-muted">
                              <i className="fa fa-user-slash fa-2x mb-2" />
                              <p className="mb-0">No operator assigned</p>
                            </div>
                          </DetailCard>
                        </div>
                      )}
                    </div>
                  </Tab.Pane>

                  {/* PARTNER TAB */}
                  <Tab.Pane eventKey="partner">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <DetailCard title="Partner Information" icon="fa-handshake">
                          <DetailRow
                            label="Partner Name"
                            value={(
                              <span className="d-flex align-items-center gap-2">
                                {order.partner?.name}
                                {order.visitor_tracking?.data?.gclid && (
                                <Badge bg="info" text="dark" style={{ fontSize: '0.7em' }}>
                                  <i className="fab fa-google me-1" />
                                  Google Ads
                                </Badge>
                                )}
                              </span>
                          )}
                          />
                          <DetailRow label="Partner Booking #" value={order.partner_booking_reference} />
                          <DetailRow
                            label="Cancellation Compensation"
                            value={`${order.partner_cancellation_price ?? 0} ${order.partner_cancellation_currency || 'EUR'}`}
                          />
                        </DetailCard>
                      </div>
                    </div>
                  </Tab.Pane>

                  {/* FINANCIAL TAB */}
                  <Tab.Pane eventKey="financial">
                    <div className="row g-3">
                      {/* Payment Information */}
                      <div className="col-md-6">
                        <DetailCard title="Pricing" icon="fa-tag">
                          <DetailRow
                            label="Base Price"
                            value={`${thousandSeparator(order.price)} ${order.currency || 'EUR'}`}
                          />
                          <DetailRow
                            label="Extras Price"
                            value={`${order.extra_price ?? 0} ${order.currency || 'EUR'}`}
                          />
                          {order.discount?.code && (
                          <>
                            <DetailRow label="Discount Code" value={order.discount.code} />
                            <DetailRow label="Discount %" value={`${order.discount.percentage}%`} />
                          </>
                          )}
                          <DetailRow
                            label="Total Price"
                            value={`${thousandSeparator(order.total_price ?? order.price)} ${order.currency || 'EUR'}`}
                            highlight
                          />
                        </DetailCard>
                      </div>

                      {/* Add-on services moved to the dedicated "Extras"
                        tab — keeps the Financial tab focused on
                        pricing / payment status / billing without
                        cluttering it when services are present. */}

                      {/* Payment Status */}
                      <div className="col-md-6">
                        <DetailCard title="Payment" icon="fa-credit-card">
                          <DetailRow label="Payment Type" value={order.payment?.type} />
                          <DetailRow
                            label="Payment Status"
                            value={(
                              <>
                                {order.payment?.paid ? (
                                  <Badge bg="success">Paid</Badge>
                                ) : (
                                  <Badge bg="warning">Unpaid</Badge>
                                )}
                                {/* Hide "(Payment Recorded)" once the original
                                    inbound payment has been refunded — the
                                    refunded label below carries the signal. */}
                                {hasInboundCompletedPayment && !hasRefundedInbound && (
                                <small className="text-muted ms-2">(Payment Recorded)</small>
                                )}
                                {hasRefundedInbound && (
                                <small className="text-muted ms-2">(Refunded)</small>
                                )}
                                {hasPartialRefund && !hasRefundedInbound && (
                                <small className="text-muted ms-2">(Partially Refunded)</small>
                                )}
                              </>
                          )}
                          />
                          {order.payment?.payment_id && (
                          <DetailRow label="Payment ID" value={order.payment.payment_id} />
                          )}
                        </DetailCard>
                      </div>

                      {/* Payment History — full ledger of Payment rows
                        attached to this booking (both Pattern A via
                        lines.booking_id and Pattern B via
                        counterparty.id). Includes a "Mark Wire
                        Received" action for manual wire/cash entries
                        unless the booking is already settled. */}
                      <div className="col-12">
                        <DetailCard title="Payment History" icon="fa-receipt">
                          <PaymentHistory
                            booking={order}
                            payments={paymentsState}
                            // Flip the legacy Paid badge without waiting for
                            // a re-fetch of the booking.
                            onRecorded={() => {
                              setOrder((prev) => ({
                                ...prev,
                                payment: { ...(prev?.payment || {}), paid: true },
                              }));
                              onChanged();
                            }}
                            onRefunded={(...args) => {
                              handleRefunded(...args);
                              onChanged();
                            }}
                            onReallocated={() => onChanged()}
                          />
                        </DetailCard>
                      </div>

                      {/* Billing Address */}
                      {order.billing && (
                      <div className="col-md-6">
                        <DetailCard title="Billing Address" icon="fa-file-invoice">
                          <div className="d-flex justify-content-end mb-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={loadStripeBilling}
                              disabled={stripeBillingBusy}
                              title="Compare with the billing address the cardholder entered in Stripe"
                            >
                              {stripeBillingBusy
                                ? <Spinner animation="border" size="sm" className="me-1" />
                                : <i className="fa fa-credit-card me-1" />}
                              Use Stripe billing details
                            </Button>
                          </div>
                          {stripeBillingError && (
                            <div className="text-danger small mb-2">{stripeBillingError}</div>
                          )}
                          <DetailRow label="Name" value={order.billing?.name} />
                          <DetailRow
                            label="Address"
                            value={`${order.billing?.address || ''}, ${order.billing?.postcode || ''}`}
                          />
                          <DetailRow
                            label="City & Country"
                            value={`${order.billing?.city || ''}, ${order.billing?.country || ''}`}
                          />
                        </DetailCard>
                      </div>
                      )}

                      {/* Metadata */}
                      <div className="col-md-6">
                        <DetailCard title="Record Info" icon="fa-info-circle">
                          <DetailRow
                            label="Created At"
                            value={order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
                          />
                          <DetailRow
                            label="Last Updated"
                            value={order.updated_at ? new Date(order.updated_at).toLocaleString() : 'N/A'}
                          />
                          <DetailRow label="Source" value={order.source?.domain} />
                          <DetailRow label="Created By" value={order.source?.created_by} />
                          <DetailRow label="Edited By" value={order.source?.edited_by} />
                          {/* Uploaded invoice PDF — only when invoicing isn't
                            managed via Billingo for this order. Partner orders
                            with invoicing enabled see it inside the Invoices
                            panel below instead. The PDF is private: the
                            opener fetches a freshly signed link on click. */}
                          {!(order.partner_id && isInvoicingEnabled(order.partner))
                            && order.invoice?.key && (
                            <DetailRow
                              label="Invoice"
                              value={(
                                <>
                                  <Button
                                    variant="link"
                                    className="p-0 align-baseline"
                                    disabled={Boolean(invoicePdf.opening)}
                                    onClick={() => invoicePdf.open({
                                      id: 'upload', type: DOC_TYPES.LEGACY, booking_ids: [order._id],
                                    })}
                                  >
                                    <i className="fa fa-external-link-alt me-1" />
                                    View Invoice
                                  </Button>
                                  {invoicePdf.error && <div className="text-danger small">{invoicePdf.error}</div>}
                                </>
                            )}
                            />
                          )}
                        </DetailCard>
                      </div>

                      {/* Billingo invoices — many typed documents per order
                        (Díjbekérő / Előlegszámla / Számla). Issued here for
                        partner orders invoiced per trip; every other booking
                        lists the documents it has (issued from Invoicing),
                        read while this tab is open. A cost record is never
                        invoiced, so it has neither. */}
                      {perTripInvoicing && (
                      <div className="col-12">
                        <div className="detail-card">
                          <div className="detail-card-body">
                            <BillingoInvoicesPanel order={order} />
                          </div>
                        </div>
                      </div>
                      )}
                      {!perTripInvoicing && !isExpense && (
                      <div className="col-12">
                        <DetailCard title="Documents" icon="fa-file-invoice">
                          {activeTab === 'financial' && (
                            <BookingDocuments order={order} onChanged={onChanged} />
                          )}
                        </DetailCard>
                      </div>
                      )}

                      {/* ...and when it is NOT enabled, say why no document is
                        raised here. Partner orders only — a direct customer
                        has no partner settlement to explain. */}
                      {order.partner_id && !perTripInvoicing && (
                      <div className="col-12">
                        <PartnerInvoicingNote partner={order.partner} />
                      </div>
                      )}
                    </div>
                  </Tab.Pane>

                  {/* EXTRAS TAB — booking add-on services. Snapshot of
                    catalog items at the time the booking was saved
                    (price + name don't drift even if the catalog item
                    is later edited). Read-only here; admins manage
                    selections from OrderInputModal's "Booking Extras"
                    section, and the full ledger view (with operator
                    summary) is the Order Ledger tab. */}
                  {Array.isArray(order.services) && order.services.length > 0 && (
                  <Tab.Pane eventKey="extras">
                    <div className="row g-3">
                      <div className="col-12">
                        <DetailCard title="Booking Extras" icon="fa-gift">
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Service</th>
                                  <th>Type</th>
                                  <th className="text-end">Unit Price</th>
                                  <th className="text-end">Quantity</th>
                                  <th className="text-end">Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.services.map((svc, idx) => (
                                  <tr
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${svc.service_id || 'svc'}-${idx}`}
                                  >
                                    <td>
                                      <strong>{svc.name}</strong>
                                      {svc.description && (
                                        <div className="small text-muted">{svc.description}</div>
                                      )}
                                    </td>
                                    <td>
                                      <Badge bg="light" text="dark">
                                        {svc.price_type === 'per_person' ? 'per person' : 'flat'}
                                      </Badge>
                                    </td>
                                    <td className="text-end">
                                      {thousandSeparator(svc.unit_price ?? 0)}
                                      {' '}
                                      {svc.currency || order.currency || 'EUR'}
                                    </td>
                                    <td className="text-end">{svc.quantity ?? 1}</td>
                                    <td className="text-end fw-bold">
                                      {thousandSeparator(svc.subtotal ?? 0)}
                                      {' '}
                                      {svc.currency || order.currency || 'EUR'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={4} className="text-end fw-bold">Total Extras</td>
                                  <td className="text-end fw-bold">
                                    {thousandSeparator(
                                      (order.services || []).reduce(
                                        (sum, s) => sum + (Number(s.subtotal) || 0),
                                        0,
                                      ),
                                    )}
                                    {' '}
                                    {order.currency || 'EUR'}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          <small className="text-muted d-block mt-2">
                            Prices are snapshots from the catalog at the time the
                            booking was saved — they don&apos;t change if the
                            catalog is later edited.
                          </small>
                        </DetailCard>
                      </div>
                    </div>
                  </Tab.Pane>
                  )}

                  {/* OTHER TAB */}
                  {(order.is_event_booking || order.event_name || order.visitor_tracking) && (
                  <Tab.Pane eventKey="other">
                    <div className="row g-3">
                      {/* Event Details */}
                      {(order.is_event_booking || order.event_name) && (
                        <div className="col-md-6">
                          <DetailCard title="Event Details" icon="fa-calendar-check">
                            <DetailRow label="Event Name" value={order.event_name} />
                            <DetailRow label="Event Group" value={order.event_group} />
                            {order.passenger_manifest && order.passenger_manifest.length > 0 && (
                              <div className="mt-3">
                                <small className="text-muted d-block mb-2">Passenger Manifest:</small>
                                <ul className="list-group list-group-flush">
                                  {order.passenger_manifest.map((p, idx) => (
                                    <li key={`manifest-${idx + 1}`} className="list-group-item py-2 px-0 bg-transparent">
                                      <strong>{p.name}</strong>
                                      {p.flight_number && (
                                        <span className="text-muted ms-2">
                                          <i className="fa fa-plane me-1" />
                                          {p.flight_number}
                                        </span>
                                      )}
                                      {p.notes && (
                                        <small className="d-block text-muted fst-italic">{p.notes}</small>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </DetailCard>
                        </div>
                      )}

                      {/* Visitor Tracking */}
                      {order.visitor_tracking && (
                        <div className="col-md-6">
                          <DetailCard title="Visitor Tracking" icon="fa-chart-line">
                            <DetailRow label="Token" value={order.visitor_tracking.token} />
                            <DetailRow
                              label="First Touch"
                              value={order.visitor_tracking.first_touch_at
                                ? new Date(order.visitor_tracking.first_touch_at).toLocaleString()
                                : 'N/A'}
                            />
                            <DetailRow
                              label="Matched At"
                              value={order.visitor_tracking.matched_at
                                ? new Date(order.visitor_tracking.matched_at).toLocaleString()
                                : 'N/A'}
                            />
                            <DetailRow label="Matched By" value={order.visitor_tracking.matched_by} />
                            {order.visitor_tracking.data?.gclid && (
                              <DetailRow label="GCLID" value={order.visitor_tracking.data.gclid} />
                            )}
                            {order.visitor_tracking.data?.utm_source && (
                              <DetailRow label="UTM Source" value={order.visitor_tracking.data.utm_source} />
                            )}
                            {order.visitor_tracking.data?.utm_medium && (
                              <DetailRow label="UTM Medium" value={order.visitor_tracking.data.utm_medium} />
                            )}
                            {order.visitor_tracking.data?.utm_campaign && (
                              <DetailRow label="UTM Campaign" value={order.visitor_tracking.data.utm_campaign} />
                            )}
                            {order.visitor_tracking.data?.referrer && (
                              <DetailRow label="Referrer" value={order.visitor_tracking.data.referrer} />
                            )}
                            {order.visitor_tracking.origin && (
                              <>
                                <DetailRow label="Origin Source" value={order.visitor_tracking.origin.source} />
                                {order.visitor_tracking.origin.specification && (
                                  <DetailRow label="Origin Detail" value={order.visitor_tracking.origin.specification} />
                                )}
                              </>
                            )}
                          </DetailCard>
                        </div>
                      )}
                    </div>
                  </Tab.Pane>
                  )}
                  {/* REMINDERS TAB */}
                  <Tab.Pane eventKey="reminders">
                    <div className="row g-3">
                      <div className="col-12">
                        <DetailCard title="Reminders" icon="fa-bell">
                          {/* Add new reminder form */}
                          <div className="mb-3 p-3 border rounded bg-light">
                            <h6 className="mb-2">Add Reminder</h6>
                            <Form.Group className="mb-2">
                              <Form.Label className="small">Title</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                placeholder="e.g. 50% payment due"
                                value={newReminderTitle}
                                onChange={(e) => setNewReminderTitle(e.target.value)}
                                maxLength={200}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="small">Due Date &amp; Time</Form.Label>
                              <Form.Control
                                type="datetime-local"
                                size="sm"
                                value={newReminderDate}
                                onChange={(e) => setNewReminderDate(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Check
                              type="checkbox"
                              label="Send agent notification when due"
                              checked={newReminderNotify}
                              onChange={(e) => setNewReminderNotify(e.target.checked)}
                              className="mb-2 small"
                            />
                            <Button
                              size="sm"
                              disabled={
                              !newReminderTitle.trim() || !newReminderDate || remindersLoading
                            }
                              onClick={async () => {
                                setRemindersLoading(true);
                                try {
                                  const updated = await BookingService.addBookingReminder(
                                    order._id,
                                    {
                                      title: newReminderTitle.trim(),
                                      due_date: new Date(newReminderDate).toISOString(),
                                      send_agent_notification: newReminderNotify,
                                    },
                                  );
                                  if (updated) setOrder(updated);
                                  setNewReminderTitle('');
                                  setNewReminderDate('');
                                  setNewReminderNotify(false);
                                } finally {
                                  setRemindersLoading(false);
                                }
                              }}
                            >
                              <i className="fa fa-plus me-1" />
                              Add Reminder
                            </Button>
                          </div>

                          {/* Existing reminders list */}
                          {(!order.notifications || order.notifications.length === 0) ? (
                            <p className="text-muted small">No reminders yet.</p>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {order.notifications.map((reminder) => {
                                const isOverdue = !reminder.is_completed
                                && new Date(reminder.due_date) <= new Date();
                                return (
                                  <div
                                    key={reminder._id}
                                    className={`p-2 border rounded d-flex align-items-start justify-content-between${reminder.is_completed ? ' opacity-50' : ''}${isOverdue ? ' border-warning' : ''}`}
                                  >
                                    <div>
                                      <div className="fw-semibold small">
                                        {reminder.is_completed && (
                                        <i className="fa fa-check-circle text-success me-1" />
                                        )}
                                        {isOverdue && (
                                        <i className="fa fa-bell text-warning me-1" />
                                        )}
                                        {reminder.title}
                                      </div>
                                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        Due:
                                        {' '}
                                        {format(reminder.due_date)}
                                        {reminder.send_agent_notification && (
                                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                          Notify agents
                                        </Badge>
                                        )}
                                      </div>
                                      {reminder.is_completed && (
                                      <div className="text-success" style={{ fontSize: '0.75rem' }}>
                                        Completed by
                                        {' '}
                                        {reminder.completed_by}
                                        {reminder.completed_at && ` at ${format(reminder.completed_at)}`}
                                      </div>
                                      )}
                                    </div>
                                    <div className="d-flex gap-1 ms-2 flex-shrink-0">
                                      {!reminder.is_completed && (
                                      <Button
                                        size="sm"
                                        variant="success"
                                        disabled={remindersLoading}
                                        title="Mark complete"
                                        onClick={async () => {
                                          setRemindersLoading(true);
                                          try {
                                            // eslint-disable-next-line max-len
                                            const updated = await BookingService.completeBookingReminder(
                                              order._id,
                                              reminder._id,
                                            );
                                            if (updated) setOrder(updated);
                                          } finally {
                                            setRemindersLoading(false);
                                          }
                                        }}
                                      >
                                        <i className="fa fa-check" />
                                      </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={remindersLoading}
                                        title="Delete reminder"
                                        onClick={async () => {
                                        // eslint-disable-next-line no-alert
                                          if (!window.confirm('Delete this reminder?')) return;
                                          setRemindersLoading(true);
                                          try {
                                          // eslint-disable-next-line max-len
                                            const updated = await BookingService.deleteBookingReminder(
                                              order._id,
                                              reminder._id,
                                            );
                                            if (updated) setOrder(updated);
                                          } finally {
                                            setRemindersLoading(false);
                                          }
                                        }}
                                      >
                                        <i className="fa fa-trash" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </DetailCard>
                      </div>
                    </div>
                  </Tab.Pane>

                  {/* DRIVER EVENTS TAB — trip claims (overtime, receipts) and the
                      terminal log. Mounted only while open: it reads the log. */}
                  <Tab.Pane eventKey="driver-events">
                    {activeTab === 'driver-events' && (
                      <DriverEventsTab
                        order={order}
                        claims={claims}
                        onCreateLineItem={openLineItem}
                      />
                    )}
                  </Tab.Pane>

                  {/* ORDER LEDGER TAB — the order lines, and the adjustments
                      to them. Mounted only while open: it reads the ledger. */}
                  <Tab.Pane eventKey="ledger">
                    {activeTab === 'ledger' && (
                      <OrderLedgerTab
                        key={lineItem?.seq ?? 0}
                        order={order}
                        payments={paymentsState.loaded ? payments : null}
                        readOnly={ledgerReadOnly}
                        prefill={lineItem?.prefill}
                        prefillNote={lineItem?.prefillNote}
                        onChanged={ledgerChanged}
                      />
                    )}
                  </Tab.Pane>

                  {/* VERSION HISTORY TAB */}
                  <Tab.Pane eventKey="version-history">
                    <div className="row g-3">
                      <div className="col-12">
                        <DetailCard title="Version History" icon="fa-history">
                          <VersionHistoryTab bookingId={order._id} />
                        </DetailCard>
                      </div>
                    </div>
                  </Tab.Pane>

                </Tab.Content>
              </Tab.Container>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <StripeBillingOverwriteModal
        show={showBillingOverwrite}
        onHide={() => setShowBillingOverwrite(false)}
        current={order.billing || {}}
        incoming={stripeBilling || {}}
        onConfirm={confirmBillingOverwrite}
        saving={stripeBillingBusy}
        error={stripeBillingError}
      />

      <PassengerMessageModal
        show={messaging.show}
        channel={messaging.channel}
        autoDraft={messaging.autoDraft}
        booking={order}
        onHide={() => setMessaging((prev) => ({ ...prev, show: false }))}
        onSent={(record) => setOrder((prev) => ({
          ...prev,
          follow_ups: [...(prev?.follow_ups || []), record],
        }))}
      />
    </>
  );
}

export default OrderDetailsModal;
